import React, { useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubble, VsiLgText, VsiMedText } from "../styled/App.styled";
import { useVsiAppContext } from "../stores/VsiApp";
import { EnvCard, EnvHeader } from "../styled/Environment";
import themeStyles from "../utils/themeStyles";
import { determineEnvBackground } from "../utils/chartUtils";
import EnvironmentProgressBar from "../components/EnvironmentProgressBar";
import { Spinner } from "reactstrap";

const VirtualEnvironmentCard = ({ keyData, name }) => {
  const { theme } = useVsiAppContext();

  const {
    total_virtual_cpus_used,
    total_virtual_cpus,
    total_memory,
    total_memory_used,
    total_disk_used,
    total_disk_allocated,
    avg_ghz_per_cpu
  } = keyData;

  const cpuUtil = Math.round((total_virtual_cpus_used / total_virtual_cpus) * 100);
  const memUtil = Math.round((total_memory_used / total_memory) * 100);
  const diskUtil = Math.round((total_disk_used / total_disk_allocated) * 100);

  const background = determineEnvBackground(Math.max(...[cpuUtil, memUtil, diskUtil]));
  const utils = [
    {
      value: cpuUtil,
      name: "CPU",
      metric: "GHZ",
      capacity: Math.round((total_virtual_cpus * avg_ghz_per_cpu)).toLocaleString()
    },
    {
      value: memUtil,
      name: "Memory",
      metric: "TiB",
      capacity: (Math.round(total_memory / 1024 * 10) / 10).toLocaleString()
    },
    {
      value: diskUtil,
      name: "Disk",
      metric: "TiB",
      capacity: (Math.round(total_disk_allocated / 1024 * 10) / 10).toLocaleString()
    }
  ];

  return (
    <EnvCard>
      <EnvHeader backgroundColor={background} style={{ borderBottom: 0 }}>
        <div className={"d-flex flex-row align-items-center justify-content-between mb-2"}>
          <div>
            <i className={`fa-solid fa-circle-nodes me-2`} style={{ color: themeStyles[theme].textColor }} />
            <VsiLgText>
              {name}
            </VsiLgText>
          </div>
          <div>
            <VsiLgText>{Math.round(total_memory * 10 / 1024) / 10?.toLocaleString?.() ?? 0} Memory
              TiB</VsiLgText>
          </div>
        </div>
        <div className={"d-flex flex-row align-items-center justify-content-between overflow-auto"}>
          {utils.map((util, index) => {
            const { value, name, metric, capacity } = util;

            return (
              <div className={"w-100 mb-1 mt-1 ms-2 me-2 "} key={`${name}-${util.name}-${index}`}>
                <div className={"d-flex flex-row align-items-center justify-content-between"}>
                  <VsiMedText textAlign={"left"}>% {name}</VsiMedText>
                  <VsiMedText textAlign={"left"}>({capacity}) {metric}</VsiMedText>
                </div>
                <EnvironmentProgressBar value={value} />
              </div>
            );
          })}
        </div>
      </EnvHeader>
    </EnvCard>
  );
};

export default function VirtualEnvironment() {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("VIRTUAL");

  const route = useMemo(() => `/api/v6/report/enterprise_virtual_all_clusters_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordset: data }, { isLoading }] = useDataWithRoute(route);

  const createEnvironmentGrouping = data => {
    const grouping = {};
    data.forEach(datum => {
      const total_memory_used = datum.total_memory * (datum.memory_util_pct / 100);
      if (!grouping[datum["vcenter_name"]]) {
        grouping[datum["vcenter_name"]] = {
          ...datum,
          total_memory_used,
          all_clusters: {
            [datum["cluster_name"].trim()]: {
              ...datum,
              total_memory_used
            }
          }
        };
      } else {
        Object.keys(datum).forEach(key => {
          if (typeof datum[key] === "number" && !key.includes("_id")) {
            const num = grouping[datum["vcenter_name"]][key];
            grouping[datum["vcenter_name"]][key] = num + datum[key];
            if (!grouping[datum["vcenter_name"]].all_clusters[datum["cluster_name"].trim()]) {
              grouping[datum["vcenter_name"]].all_clusters[datum["cluster_name"].trim()] = { ...datum };
            }
          }
        });
      }
    });
    return grouping;
  };

  const { envData, envKeys } = useMemo(() => {
    const envData = createEnvironmentGrouping(data);
    const envKeys = Object.keys(envData) || [];
    return {
      envData,
      envKeys
    }
  }, [data]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble className={"mb-2 overflow-hidden"} style={{ height: 400 }}>
      <div className={"w-100 px-2"}>
        {envKeys.map((key, index) => {
          const keyData = envData[key];
          return (
            <VirtualEnvironmentCard key={`left-column-${key}-${index}`}
                                    keyData={keyData}
                                    name={key} />
          );
        })}
      </div>
    </VsiBubble>
  );
}
